// src/pages/inventory/commerce/components/drawerTabs/ShopifyDetailsTab.tsx
import React from "react";
import { Descriptions, Space, Typography, Button, Alert, Divider } from "antd";
import { ShoppingOutlined, LinkOutlined, SyncOutlined, CheckCircleOutlined } from "@ant-design/icons";
import type { Product } from "../../../../../types/inventory/ProductTypes";
import { SectionCard, StatusTag } from "../../../../../components/shared";

const { Text, Title, Paragraph } = Typography;

interface ShopifyDetailsTabProps {
	product: Product;
	setProduct: React.Dispatch<React.SetStateAction<Product>>;
}

const ShopifyDetailsTab: React.FC<ShopifyDetailsTabProps> = ({ product }) => {
	const isConnected = !!product.product_shopify_id;
	const needsSync = product.sync_to_shopify && product.last_shopify_sync_at ? new Date(product.updated_at || "") > new Date(product.last_shopify_sync_at) : false;

	return (
		<div>
			<Space direction="vertical" style={{ width: "100%" }} size="large">
				{!isConnected ? (
					<Alert
						message="Not Connected to Shopify"
						description="This product is not currently synced with Shopify. To connect it, enable 'Sync to Shopify' in the Product Details tab and save. The product will be created in Shopify on the next sync."
						type="info"
						showIcon
						icon={<ShoppingOutlined />}
					/>
				) : (
					<>
						<Alert
							message={
								<Space>
									<CheckCircleOutlined />
									Connected to Shopify
								</Space>
							}
							description={`This product is linked to Shopify Product #${product.product_shopify_id}`}
							type="success"
							showIcon
						/>

						{needsSync && (
							<Alert
								message="Sync Required"
								description="Local changes have been made since the last Shopify sync. Click the sync button below to update Shopify."
								type="warning"
								showIcon
								icon={<SyncOutlined spin />}
								action={
									<Button size="small" type="primary" icon={<SyncOutlined />}>
										Sync Now
									</Button>
								}
							/>
						)}
					</>
				)}

				<SectionCard title="Shopify Information">
					<Descriptions column={1} bordered>
						<Descriptions.Item label="Shopify Product ID">{product.product_shopify_id || "—"}</Descriptions.Item>
						<Descriptions.Item label="Shopify Variant ID">{product.shopify_variant_id || "—"}</Descriptions.Item>
						<Descriptions.Item label="Shopify Inventory Item ID">{product.shopify_inventory_item_id || "—"}</Descriptions.Item>
						<Descriptions.Item label="Handle">
							{product.shopify_handle ? (
								<Space>
									<Text code>{product.shopify_handle}</Text>
									{product.shopify_handle && (
										<Button type="link" size="small" icon={<LinkOutlined />} href={`https://admin.shopify.com/store/products/${product.shopify_handle}`} target="_blank">
											View in Shopify
										</Button>
									)}
								</Space>
							) : (
								"—"
							)}
						</Descriptions.Item>
						<Descriptions.Item label="Sync Status">
							{product.sync_to_shopify ? (
								<StatusTag status="active" label="Enabled" />
							) : (
								<StatusTag status="inactive" label="Disabled" />
							)}
						</Descriptions.Item>
						<Descriptions.Item label="Last Synced">{product.last_shopify_sync_at ? new Date(product.last_shopify_sync_at).toLocaleString() : "Never"}</Descriptions.Item>
					</Descriptions>
				</SectionCard>

				{product.variants && product.variants.length > 0 && (
					<SectionCard title="Product Variants">
						<Space direction="vertical" style={{ width: "100%" }}>
							{product.variants.map((variant, index) => (
								<SectionCard key={variant.id}>
									<Descriptions column={2} size="small">
										<Descriptions.Item label="Title">{variant.title || variant.formatted_title || "Default"}</Descriptions.Item>
										<Descriptions.Item label="SKU">{variant.sku || "—"}</Descriptions.Item>
										<Descriptions.Item label="Price">${variant.price ? Number(variant.price).toFixed(2) : "0.00"}</Descriptions.Item>
										<Descriptions.Item label="Inventory">{variant.inventory_quantity || 0} units</Descriptions.Item>
										{variant.shopify_variant_id && <Descriptions.Item label="Shopify Variant ID">{variant.shopify_variant_id}</Descriptions.Item>}
										<Descriptions.Item label="Status">
											<StatusTag status={variant.is_active ? "active" : "inactive"} />
										</Descriptions.Item>
									</Descriptions>
								</SectionCard>
							))}
						</Space>
					</SectionCard>
				)}

				<Divider />

				<div>
					<Title level={5}>Sync Information</Title>
					<Paragraph type="secondary">
						Products are automatically synced with Shopify when changes are made. You can also trigger manual syncs from the Shopify Integration page. When syncing:
						<ul>
							<li>Product name, description, and pricing are updated</li>
							<li>Inventory levels are synchronized</li>
							<li>Product images are uploaded</li>
							<li>Variants are created and managed</li>
						</ul>
					</Paragraph>
				</div>
			</Space>
		</div>
	);
};

export default ShopifyDetailsTab;
